import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Card, CardContent } from "../components/ui/card";
import { Separator } from "../components/ui/separator";
import { Sheet, SheetContent, SheetTrigger } from "../components/ui/sheet";
import SblChatWidget from "../components/SblChatWidget";
import LandingFlashCard from "../components/LandingFlashCard";
import { SBL_PROGRAMMES } from "../lib/programmes";
import { APPLY_GOOGLE_FORM_URL } from "../lib/applyLinks";
import {
  ArrowRight, CheckCircle, Users, Brain, Trophy, Target, Eye, Lightbulb,
  Envelope, MapPin, Image as ImageIcon, List, X,
  LinkedinLogo, FacebookLogo, InstagramLogo, YoutubeLogo,
  CaretDown, Quotes, Buildings, Handshake, Shield, Rocket, ArrowUpRight
} from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "../lib/api";
import { isEnrolling, isInactive, statusTag, statusBadgeClass, isPublic } from "../lib/programmeStatus";

const SBL_LOGO = "/sbl-logo.jpeg";

const FEATURES = [
  { icon: "graduation", title: "10 Specialist Programmes", desc: "From foundational leadership to executive transformation — 20 courses covering every system that makes great schools work." },
  { icon: "brain", title: "AI-Powered Learning", desc: "Smart quiz generation, lesson summaries, and a personal AI assistant to accelerate your growth." },
  { icon: "self-paced", title: "Flexible & Self-Paced", desc: "3, 6, or 12-month tracks. Learn at your own pace with structured milestones and weekly deep-dives." },
  { icon: "trophy", title: "Graded Assessments & Certification", desc: "MCQ assessments, capstone projects, and verifiable certificates with Distinction, Merit, or Participation rankings." },
  { icon: "users", title: "Expert Instructors & Mentors", desc: "Learn from seasoned school leaders with decades of real-world experience across Africa and beyond." },
  { icon: "community", title: "Accountability Partners & Community", desc: "Paired with a fellow participant for mutual support. Join a growing network of school builders across Africa." },
  { icon: "operations", title: "Practical, Not Theoretical", desc: "Build real SOPs, HR systems, financial models, growth plans, and safeguarding frameworks — not just theories." },
  { icon: "live", title: "Live Classes & Discussion Forums", desc: "Interactive live sessions with instructors, peer discussions, polls, and collaborative problem-solving." },
];

import { LANDING_TESTIMONIALS } from "./TestimonialsPage";

function FeatureIcon({ type }) {
  const iconMap = { graduation: Trophy, brain: Brain, trophy: CheckCircle, users: Users, "self-paced": Lightbulb, community: Target, operations: Eye, live: Lightbulb };
  const Icon = iconMap[type] || Trophy;
  return <Icon size={20} weight="bold" className="text-white" />;
}

export default function LandingPage() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", programme_interest: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [gallery, setGallery] = useState([]);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [exchangeRate, setExchangeRate] = useState(null);
  const [content, setContent] = useState({});
  const [programmes, setProgrammes] = useState([]);
  const c = (key, field, fallback) => (content[key]?.[field] ?? "") || fallback;
  const fetchGallery = () => {
    api.get("/gallery").then(r => setGallery(r.data)).catch(() => {});
  };

  useEffect(() => {
    fetchGallery();
    api.get("/exchange-rate").then(r => setExchangeRate(r.data?.usd_to_ngn)).catch(() => {});
    api.get("/site-content").then(r => {
      const m = {};
      (r.data || []).forEach(s => { m[s.key] = { heading: s.heading, subheading: s.subheading, body: s.body }; });
      setContent(m);
    }).catch(() => {});
    // Live programme catalogue — filter out anything that isn't public-safe,
    // then sort by code so the landing tiles are deterministic.
    // Public catalogue feed — uses /public-programmes which already filters
    // out admin-created LMS offerings server-side and sorts by code.
    api.get("/public-programmes").then(r => {
      const data = (r.data || [])
        .filter(isPublic)
        .sort((a, b) => (a.code || "").localeCompare(b.code || ""));
      setProgrammes(data);
    }).catch(() => setProgrammes([]));
  }, []);

  // When users land on /#contact (e.g. via "Make Enquiry" on a non-enrolling
  // programme), scroll past the hero straight to the contact form. Runs once
  // the page has rendered enough to know the section exists.
  useEffect(() => {
    if (typeof window === "undefined") return;
    if ((window.location.hash || "").toLowerCase() !== "#contact") return;
    const scrollToContact = () => {
      const el = document.getElementById("contact");
      if (el) { el.scrollIntoView({ behavior: "smooth", block: "start" }); return true; }
      return false;
    };
    // Try a few times in case the section hasn't mounted yet (gallery / cms fetches).
    let tries = 0;
    const t = setInterval(() => {
      if (scrollToContact() || ++tries > 20) clearInterval(t);
    }, 150);
    return () => clearInterval(t);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Please provide your name and email.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/interest", form);
      setSubmitted(true);
      toast.success("Thank you! We'll be in touch shortly.");
    } catch {
      toast.error("Something went wrong. Please try again.");
    }
    setSubmitting(false);
  };

  return (
    <div className="min-h-screen bg-white" data-testid="landing-page">
      {/* ─── NAVIGATION ─── */}
      <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-xl border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src={SBL_LOGO} alt="School Builder Lab Africa" className="w-9 h-9 object-contain rounded-sm" />
            <span className="text-base sm:text-lg font-bold tracking-tight text-slate-950" style={{ fontFamily: "'Outfit', sans-serif" }}>
              School Builder Lab Africa
            </span>
          </Link>
          <nav className="hidden md:flex items-center gap-1 lg:gap-2 text-xs lg:text-sm font-medium text-slate-600">
            <a href="#top" className="px-3 py-2 hover:text-slate-950 transition-colors">Home</a>

            {/* Programmes dropdown — consolidated hub for all learning pathways */}
            <div className="relative" onMouseEnter={() => setOpenDropdown("learn")} onMouseLeave={() => setOpenDropdown(null)}>
              <button className="px-3 py-2 hover:text-slate-950 transition-colors flex items-center gap-1" data-testid="nav-learn-dropdown">
                Programmes <CaretDown size={12} />
              </button>
              {openDropdown === "learn" && (
                <div className="absolute top-full left-0 bg-white border border-slate-200 shadow-lg py-1 min-w-[220px] z-50">
                  <Link to="/programmes-catalogue" className="block px-4 py-2 text-sm hover:bg-slate-50 text-slate-700 hover:text-slate-950" data-testid="nav-all-programmes-link">All Programmes</Link>
                  <a href="#programmes" className="block px-4 py-2 text-sm hover:bg-slate-50 text-slate-700 hover:text-slate-950">Overview</a>
                  <Link to="/courses" className="block px-4 py-2 text-sm hover:bg-slate-50 text-blue-800 font-semibold hover:text-slate-950" data-testid="nav-courses-link">Courses</Link>
                  <Link to="/free-courses" className="block px-4 py-2 text-sm hover:bg-slate-50 text-slate-700 hover:text-slate-950">Free Courses</Link>
                  <div className="h-px bg-slate-100 my-1" />
                  <Link to="/pod" className="block px-4 py-2 text-sm hover:bg-slate-50 text-amber-700 font-semibold hover:text-slate-950" data-testid="nav-pod-link">SBL POD</Link>
                  <Link to="/leadership-sprint" className="block px-4 py-2 text-sm hover:bg-slate-50 text-purple-700 font-semibold hover:text-slate-950" data-testid="nav-sprint-link">Leadership Sprint</Link>
                </div>
              )}
            </div>

            {/* About dropdown */}
            <div className="relative" onMouseEnter={() => setOpenDropdown("about")} onMouseLeave={() => setOpenDropdown(null)}>
              <button className="px-3 py-2 hover:text-slate-950 transition-colors flex items-center gap-1" data-testid="nav-about-dropdown">
                About <CaretDown size={12} />
              </button>
              {openDropdown === "about" && (
                <div className="absolute top-full left-0 bg-white border border-slate-200 shadow-lg py-1 min-w-[180px] z-50">
                  <a href="#features" className="block px-4 py-2 text-sm hover:bg-slate-50 text-slate-700 hover:text-slate-950">Why Us?</a>
                  <a href="#vision" className="block px-4 py-2 text-sm hover:bg-slate-50 text-slate-700 hover:text-slate-950">Vision & Values</a>
                  <Link to="/team" className="block px-4 py-2 text-sm hover:bg-slate-50 text-slate-700 hover:text-slate-950">Meet the Team</Link>
                  <Link to="/team#board" className="block px-4 py-2 text-sm hover:bg-slate-50 text-slate-700 hover:text-slate-950">Board of Advisors</Link>
                  <Link to="/gallery" className="block px-4 py-2 text-sm hover:bg-slate-50 text-slate-700 hover:text-slate-950">Gallery</Link>
                  <Link to="/testimonials" className="block px-4 py-2 text-sm hover:bg-slate-50 text-slate-700 hover:text-slate-950">Testimonials</Link>
                </div>
              )}
            </div>

            <Link to="/careers" className="px-3 py-2 hover:text-slate-950 transition-colors">Vacancies</Link>
            <a href="#contact" className="px-3 py-2 hover:text-slate-950 transition-colors">Contact</a>
          </nav>
          <div className="flex items-center gap-2 sm:gap-3">
            <Link to="/login" className="hidden sm:inline-block">
              <Button variant="ghost" className="rounded-none text-sm font-medium text-slate-700 hover:text-slate-950 px-3" data-testid="nav-login-btn">
                Sign In
              </Button>
            </Link>
            <a href="https://forms.gle/m9cTe5dHXwx95LcdA" target="_blank" rel="noopener noreferrer" className="hidden sm:inline-block">
              <Button className="bg-blue-800 text-white hover:bg-blue-900 rounded-none text-sm font-medium px-3 sm:px-5" data-testid="nav-apply-btn">
                Apply Now
              </Button>
            </a>
            {/* Mobile Menu */}
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild className="md:hidden">
                <Button variant="ghost" size="sm" data-testid="mobile-nav-menu-btn">
                  <List size={24} />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-72 p-0">
                <div className="flex flex-col h-full">
                  <div className="p-5 pb-4 flex items-center gap-2">
                    <img src={SBL_LOGO} alt="School Builder Lab Africa" className="w-8 h-8 object-contain rounded-sm" />
                    <span className="text-sm font-bold tracking-tight text-slate-950" style={{ fontFamily: "'Outfit', sans-serif" }}>School Builder Lab Africa</span>
                  </div>
                  <Separator />
                  <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
                    <p className="px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-widest text-slate-400">Navigate</p>
                    <a href="#top" onClick={() => setMobileMenuOpen(false)} className="block px-3 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-100">Home</a>
                    <p className="px-3 pt-4 pb-1 text-[10px] font-semibold uppercase tracking-widest text-slate-400">Programmes</p>
                    <a href="#programmes" onClick={() => setMobileMenuOpen(false)} className="block px-3 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-100">Our Programmes</a>
                    <Link to="/programmes-catalogue" onClick={() => setMobileMenuOpen(false)} className="block px-3 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-100">All Programmes</Link>
                    <Link to="/courses" onClick={() => setMobileMenuOpen(false)} className="block px-3 py-2.5 text-sm font-bold text-blue-800 hover:bg-slate-100">Courses →</Link>
                    <Link to="/free-courses" onClick={() => setMobileMenuOpen(false)} className="block px-3 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-100">Free Courses</Link>
                    <Link to="/pod" onClick={() => setMobileMenuOpen(false)} className="block px-3 py-2.5 text-sm font-bold text-amber-700 hover:bg-slate-100">SBL POD →</Link>
                    <Link to="/leadership-sprint" onClick={() => setMobileMenuOpen(false)} className="block px-3 py-2.5 text-sm font-bold text-purple-700 hover:bg-slate-100">Leadership Sprint →</Link>
                    <p className="px-3 pt-4 pb-1 text-[10px] font-semibold uppercase tracking-widest text-slate-400">About</p>
                    <a href="#features" onClick={() => setMobileMenuOpen(false)} className="block px-3 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-100">Why Us?</a>
                    <a href="#vision" onClick={() => setMobileMenuOpen(false)} className="block px-3 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-100">Vision & Values</a>
                    <Link to="/team" onClick={() => setMobileMenuOpen(false)} className="block px-3 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-100">Meet the Team</Link>
                    <Link to="/team#board" onClick={() => setMobileMenuOpen(false)} className="block px-3 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-100">Board of Advisors</Link>
                    <Link to="/gallery" onClick={() => setMobileMenuOpen(false)} className="block px-3 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-100">Gallery</Link>
                    <Link to="/testimonials" onClick={() => setMobileMenuOpen(false)} className="block px-3 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-100">Testimonials</Link>
                    <p className="px-3 pt-4 pb-1 text-[10px] font-semibold uppercase tracking-widest text-slate-400">More</p>
                    <Link to="/careers" onClick={() => setMobileMenuOpen(false)} className="block px-3 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-100">Vacancies</Link>
                    <a href="#contact" onClick={() => setMobileMenuOpen(false)} className="block px-3 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-100">Contact</a>
                  </nav>
                  <Separator />
                  <div className="p-4 space-y-2">
                    <Link to="/login" onClick={() => setMobileMenuOpen(false)}>
                      <Button variant="outline" className="w-full rounded-none text-sm font-medium">Sign In</Button>
                    </Link>
                    <a href="https://forms.gle/m9cTe5dHXwx95LcdA" target="_blank" rel="noopener noreferrer" onClick={() => setMobileMenuOpen(false)}>
                      <Button className="w-full bg-blue-800 text-white hover:bg-blue-900 rounded-none text-sm font-medium mt-2">Apply Now</Button>
                    </a>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>

      {/* ─── HERO ─── */}
      <section id="top" className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://static.prod-images.emergentagent.com/jobs/8f8a1e34-b7b5-4fc0-b7ca-0a56778fb2cb/images/74b52de1c3edfe940fd688e7e695f0aa82437d308d3f65927c1ab51bbf4c30a1.png"
            alt="Education Leadership boardroom"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-slate-950/65" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 py-20 sm:py-28 md:py-36 lg:py-44">
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-gold-400 mb-4">{c("hero","subheading","Africa's First Dedicated School Leadership Academy")}</p>
          <h1
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-white leading-[1.1] max-w-4xl mb-6"
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            {c("hero","heading","We build the leaders the classroom forgot to train.")}
          </h1>
          <p className="text-base sm:text-lg text-white/85 max-w-2xl mb-8 sm:mb-10 leading-relaxed" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
            {c("hero","body","School Builder Lab Africa is Africa's first dedicated academy for non-instructive school leadership — operations, finance, growth, governance, welfare, technology and culture. Specialised programmes for proprietors, principals, administrators and the support staff who keep institutions running.")}
          </p>
          <div className="flex flex-wrap gap-3 sm:gap-4">
            <a href={APPLY_GOOGLE_FORM_URL} target="_blank" rel="noopener noreferrer">
              <Button className="bg-gold-500 text-slate-950 hover:bg-gold-600 rounded-none text-sm font-semibold px-6 sm:px-8 h-11 sm:h-12 transition-all active:scale-[0.98]" data-testid="hero-apply-btn">
                Apply Now <ArrowRight size={16} className="ml-2" />
              </Button>
            </a>
            <Link to="/programmes-catalogue">
              <Button variant="outline" className="rounded-none text-sm font-medium px-6 sm:px-8 h-11 sm:h-12 border-white/30 text-white hover:bg-white/10 hover:text-white" data-testid="hero-programmes-btn">
                Explore Programme
              </Button>
            </Link>
            <Link to="/pod">
              <Button variant="outline" className="rounded-none text-sm font-medium px-6 sm:px-8 h-11 sm:h-12 border-amber-400 text-amber-300 hover:bg-amber-500/10" data-testid="hero-pod-btn">
                Join SBL POD →
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* ─── STATS BAR ─── */}
      <section className="bg-slate-900 border-y border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8 grid grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8 text-center">
          {[
            { value: "28", label: "NQF-Aligned Programmes" },
            { value: "NQF 4-8", label: "Levels Covered" },
            { value: "7", label: "Core Leadership Domains" },
            { value: "Africa-First", label: "Non-Instructive Academy" },
          ].map((s, i) => (
            <div key={i}>
              <p className="text-xl sm:text-2xl md:text-3xl font-bold text-white tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>{s.value}</p>
              <p className="text-[10px] sm:text-xs uppercase tracking-widest text-slate-400 mt-1">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ─── VISION, MISSION & CORE VALUES ─── */}
      <section id="vision" className="py-16 sm:py-20 md:py-28 bg-white border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12 sm:mb-16">
            <p className="text-xs font-semibold uppercase tracking-widest text-blue-800 mb-2">Who We Are</p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-950" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Our Vision, Mission & Core Values
            </h2>
          </div>

          {/* Vision & Mission Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
            <div className="bg-slate-950 text-white p-8 sm:p-10">
              <div className="w-12 h-12 rounded-full bg-amber-400/20 flex items-center justify-center mb-5">
                <Eye size={24} className="text-amber-400" weight="fill" />
              </div>
              <h3 className="text-lg font-bold mb-3 tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>{c("vision","heading","Our Vision")}</h3>
              <p className="text-sm text-white/85 leading-relaxed">
                {c("vision","body","To become Africa's leading academy for non-instructive school leadership — preparing the operators, administrators, growth strategists, finance leads and welfare champions who quietly hold institutions together.")}
              </p>
            </div>
            <div className="bg-blue-800 text-white p-8 sm:p-10">
              <div className="w-12 h-12 rounded-full bg-white/15 flex items-center justify-center mb-5">
                <Target size={24} className="text-white" weight="fill" />
              </div>
              <h3 className="text-lg font-bold mb-3 tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>{c("mission","heading","Our Mission")}</h3>
              <p className="text-sm text-white/85 leading-relaxed">
                {c("mission","body","To equip school professionals — proprietors, principals, administrators, support staff and aspiring leaders — with the systems, credentials and confidence to build, run and grow institutions that work. Every programme is built around real-world African schools.")}
              </p>
            </div>
          </div>

          {/* The Problem We Solve */}
          <div className="bg-amber-50 border-l-4 border-amber-500 p-6 sm:p-8 mb-12">
            <p className="text-xs uppercase tracking-widest text-amber-800 font-bold mb-2">The Problem We Solve</p>
            <p className="text-sm sm:text-base text-slate-800 leading-relaxed">
              Most school leadership training focuses on teaching — but only <b>20% of running a school is in the classroom</b>. The other 80% — operations, finance, marketing, HR, technology, welfare, safeguarding, governance, growth — has nowhere to learn from. We exist to fix that.
            </p>
          </div>

          {/* Core Values - BUILD */}
          <div className="text-center mb-8">
            <h3 className="text-lg sm:text-xl font-bold text-slate-950 tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Our Core Values &mdash; <span className="text-blue-800">BUILD</span>
            </h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {[
              { letter: "B", word: "Build", desc: "We create systems that work.", icon: Buildings },
              { letter: "U", word: "Uplift", desc: "We support and develop people.", icon: Handshake },
              { letter: "I", word: "Integrity", desc: "We do what is right.", icon: Shield },
              { letter: "L", word: "Lead", desc: "We take responsibility and drive change.", icon: Users },
              { letter: "D", word: "Deliver", desc: "We focus on results and real impact.", icon: Rocket },
            ].map((v, i) => (
              <div key={i} className="border border-slate-200 bg-white p-5 sm:p-6 text-center hover:border-blue-800/30 hover:shadow-md transition-all group" data-testid={`core-value-${i}`}>
                <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-blue-800/10 flex items-center justify-center group-hover:bg-blue-800 transition-colors">
                  <v.icon size={22} className="text-blue-800 group-hover:text-white transition-colors" weight="fill" />
                </div>
                <div className="text-2xl font-black text-blue-800 mb-0.5" style={{ fontFamily: "'Outfit', sans-serif" }}>{v.letter}</div>
                <p className="text-sm font-semibold text-slate-900 mb-1">{v.word}</p>
                <p className="text-xs text-slate-500 leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── PROGRAMMES ─── */}
      <section id="programmes" className="py-16 sm:py-20 md:py-28 bg-slate-50 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <p className="text-xs font-semibold uppercase tracking-widest text-blue-800 mb-2">Our Programmes · {programmes.length || 28} Pathways</p>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-950 mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
            From Foundation Certificates to Executive Fellowships
          </h2>
          <p className="text-base text-slate-600 max-w-2xl mb-10 sm:mb-12">A wide range of NQF-aligned programmes across Foundation Certificates (Level 4), Professional Certificates & Diplomas (Levels 5-6), Advanced Diplomas (Level 7), and Fellowship Programmes (Level 8). For every role, every career stage.</p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {programmes.slice(0, 6).map((p, i) => {
              // Single source of truth (Round 30) — programmeStatus helper.
              const enrolling = isEnrolling(p);
              const tag = statusTag(p);
              const tagline = [p.level, p.nqf_level].filter(Boolean).join(" · ");
              const greyed = !enrolling;
              return (
              <Link key={p.code || i} to={p.code ? `/p/${p.code.toLowerCase()}` : "/programmes-catalogue"} className="block" data-testid={`landing-programme-link-${i}`}>
              <Card className={`border border-slate-200 rounded-none shadow-sm hover:border-slate-400 hover:shadow-md transition-all group bg-white cursor-pointer h-full ${greyed ? "opacity-70" : ""}`} data-testid={`landing-programme-${i}`}>
                <CardContent className="p-5">
                  <div className="flex items-center gap-2 mb-3 flex-wrap">
                    <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 ${statusBadgeClass(p)}`}>{tag}</span>
                    {p.code && <span className="text-[10px] font-bold uppercase tracking-widest text-amber-700">{p.code}</span>}
                    <span className="text-xs text-slate-500">{p.duration}</span>
                  </div>
                  <h3 className="text-sm sm:text-base font-semibold text-slate-900 mb-1 group-hover:text-blue-800 transition-colors leading-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>
                    {p.title}
                  </h3>
                  <p className="text-xs text-slate-500 italic">{tagline}</p>
                  {p.price_usd ? (
                    <div className="mt-3 pt-3 border-t border-slate-100">
                      <p className="text-lg font-bold text-slate-950" style={{ fontFamily: "'Outfit', sans-serif" }}>
                        ${p.price_usd}
                        {exchangeRate && <span className="text-xs text-slate-500 font-normal ml-1">≈ ₦{Math.round(p.price_usd * exchangeRate).toLocaleString()}</span>}
                      </p>
                      <p className="text-[10px] text-slate-500 uppercase tracking-wider">Programme fee</p>
                    </div>
                  ) : null}
                </CardContent>
              </Card>
              </Link>
              );
            })}
          </div>
          <div className="mt-6 text-center">
            <Link to="/programmes-catalogue">
              <Button variant="outline" className="rounded-none gap-2 text-sm" data-testid="view-all-programmes-btn">
                See all {programmes.length || 28} programmes <ArrowUpRight size={14} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* ─── SBL LEADERSHIP POD ─── */}
      <section id="pod" className="py-16 sm:py-20 md:py-28 bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900 text-white relative overflow-hidden">
        <div className="absolute -top-32 -right-32 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl" />
        <div className="absolute -bottom-32 -left-32 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.25em] text-amber-300 mb-3">SBL Leadership POD</p>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-5 leading-[1.1]" style={{ fontFamily: "'Outfit', sans-serif" }}>
                Leadership beyond the classroom. Building sustainable schools together.
              </h2>
              <p className="text-base text-white/80 leading-relaxed mb-6">
                Can't commit to the full Fellowship? Join the <b>SBL Leadership POD</b> — a professional growth community for educators, school leaders, online tutors and education entrepreneurs. Monthly webinars, a growing resource library, quarterly roundtables and visibility opportunities.
              </p>
              <ul className="space-y-2 mb-8 text-sm text-white/85">
                {[
                  "Monthly Webinars — Leadership, Career, Tech, Money, Tutoring",
                  "Resource Library — 100+ templates and growing",
                  "Quarterly Virtual Roundtables for peer learning",
                  "Visibility opportunities & professional brand building",
                  "Pay quarterly (₦30k), bi-annually (₦55k) or annually (₦100k)",
                ].map(b => (
                  <li key={b} className="flex items-start gap-2"><CheckCircle weight="fill" size={16} className="text-amber-300 mt-0.5 flex-shrink-0" /> {b}</li>
                ))}
              </ul>
              <div className="flex gap-3 flex-wrap">
                <Link to="/pod">
                  <Button className="bg-amber-500 text-slate-950 hover:bg-amber-400 rounded-none gap-2 px-6 py-6" data-testid="landing-pod-cta">
                    Explore the POD <ArrowRight weight="bold" size={16} />
                  </Button>
                </Link>
                <Link to="/pod/signup">
                  <Button variant="outline" className="border-white/30 text-white hover:bg-white hover:text-slate-950 rounded-none px-6 py-6 bg-transparent" data-testid="landing-pod-join">
                    Join Now
                  </Button>
                </Link>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {[
                { label: "Monthly", value: "Webinars", desc: "Live & recorded" },
                { label: "Quarterly", value: "Roundtables", desc: "Peer learning" },
                { label: "Growing", value: "Resources", desc: "Templates · Tools" },
                { label: "Visibility", value: "Opportunities", desc: "Brand · Speaking" },
              ].map(card => (
                <div key={card.label} className="bg-white/5 border border-white/15 backdrop-blur-sm p-5">
                  <p className="text-[10px] uppercase tracking-widest text-amber-300 font-bold mb-2">{card.label}</p>
                  <p className="text-xl sm:text-2xl font-bold mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>{card.value}</p>
                  <p className="text-xs text-white/60">{card.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ─── LEADERSHIP & INNOVATION SPRINT (Round 40 · Task 7) ─── */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-indigo-950 via-purple-950 to-slate-950 text-white relative overflow-hidden" data-testid="landing-sprint-section">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: "radial-gradient(circle at 20% 20%, #fbbf24 0, transparent 40%), radial-gradient(circle at 80% 80%, #a855f7 0, transparent 40%)" }} />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-amber-300 mb-3">New · Summer Cohort</p>
            <h2 className="text-2xl sm:text-4xl font-bold mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Summer Leadership & Innovation Sprint
            </h2>
            <p className="text-lg font-semibold text-amber-300 mb-4">Empowering educators to lead, innovate and thrive.</p>
            <p className="text-sm sm:text-base text-purple-100 leading-relaxed mb-5">
              A 4-week live online cohort for teachers, school leaders and education entrepreneurs. Master AI-powered classroom tools, sharpen your leadership brand, unlock new income streams, and launch a school improvement initiative — all in one term.
            </p>
            <Link
              to="/leadership-sprint"
              className="inline-flex items-center gap-2 px-5 py-3 bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold text-sm rounded-none"
              data-testid="landing-sprint-cta"
            >
              Learn More <ArrowRight weight="bold" size={16} />
            </Link>
          </div>
          <div className="bg-white/5 backdrop-blur border border-white/10 p-8">
            <div className="grid grid-cols-3 gap-4 mb-6">
              <div><p className="text-[10px] uppercase tracking-widest text-amber-300 font-bold">Duration</p><p className="text-lg font-bold mt-1">4 weeks</p></div>
              <div><p className="text-[10px] uppercase tracking-widest text-amber-300 font-bold">Format</p><p className="text-lg font-bold mt-1">Live online</p></div>
              <div><p className="text-[10px] uppercase tracking-widest text-amber-300 font-bold">From</p><p className="text-lg font-bold mt-1">₦20,000</p></div>
            </div>
            <ul className="space-y-2 text-sm text-slate-200">
              <li className="flex gap-2"><span className="text-amber-300">›</span>AI, automation & digital creativity for educators</li>
              <li className="flex gap-2"><span className="text-amber-300">›</span>Leadership brand, CV & LinkedIn optimisation</li>
              <li className="flex gap-2"><span className="text-amber-300">›</span>New income streams inside education</li>
              <li className="flex gap-2"><span className="text-amber-300">›</span>Lead a real school improvement initiative</li>
            </ul>
          </div>
        </div>
      </section>

      {/* ─── WHY US? ─── */}
      <section id="features" className="py-16 sm:py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <p className="text-xs font-semibold uppercase tracking-widest text-blue-800 mb-2">Why School Builder Lab Africa?</p>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-950 mb-10 sm:mb-12" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Built for Serious Educational Leadership Development
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {FEATURES.map((f, i) => (
              <div key={i} className="bg-slate-50 border border-slate-200 p-5 sm:p-6 hover:border-slate-400 hover:shadow-md transition-all" data-testid={`landing-feature-${i}`}>
                <div className="w-10 h-10 bg-blue-800 flex items-center justify-center mb-4">
                  <FeatureIcon type={f.icon} />
                </div>
                <h3 className="text-sm sm:text-base font-semibold text-slate-900 mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>{f.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── GALLERY ─── */}
      <section id="gallery" className="py-16 sm:py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-end justify-between mb-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest text-blue-800 mb-2">Gallery</p>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-950" style={{ fontFamily: "'Outfit', sans-serif" }}>
                Moments from Our Educational Community
              </h2>
            </div>
            <Link to="/gallery">
              <Button variant="outline" className="rounded-none gap-2 text-sm hidden sm:inline-flex" data-testid="view-full-gallery-btn">
                View Full Gallery <ArrowUpRight size={14} />
              </Button>
            </Link>
          </div>
          <p className="text-base text-slate-600 max-w-2xl mb-10 sm:mb-12">A glimpse into the vibrant educational leadership experiences at School Builder Lab Africa.</p>

          {gallery.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4">
              {gallery.slice(0, 6).map((item, i) => (
                <div key={item.id} className="group relative overflow-hidden border border-slate-200 aspect-square" data-testid={`gallery-item-${i}`}>
                  <img
                    src={item.image_url || item.image_path}
                    alt={item.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute bottom-0 left-0 right-0 bg-slate-950/70 px-3 py-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <p className="text-xs text-white font-medium truncate">{item.title || ""}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4">
              {[
                "https://images.pexels.com/photos/3184291/pexels-photo-3184291.jpeg?auto=compress&cs=tinysrgb&w=600",
                "https://images.pexels.com/photos/3184325/pexels-photo-3184325.jpeg?auto=compress&cs=tinysrgb&w=600",
                "https://images.pexels.com/photos/3184360/pexels-photo-3184360.jpeg?auto=compress&cs=tinysrgb&w=600",
                "https://images.pexels.com/photos/3184418/pexels-photo-3184418.jpeg?auto=compress&cs=tinysrgb&w=600",
                "https://images.pexels.com/photos/3184339/pexels-photo-3184339.jpeg?auto=compress&cs=tinysrgb&w=600",
                "https://images.pexels.com/photos/3184405/pexels-photo-3184405.jpeg?auto=compress&cs=tinysrgb&w=600",
              ].map((src, i) => (
                <div key={i} className="relative overflow-hidden border border-slate-200 aspect-square">
                  <img src={src} alt={`School Builder Lab Africa community ${i+1}`} className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
                </div>
              ))}
            </div>
          )}
          <div className="mt-8 text-center sm:hidden">
            <Link to="/gallery">
              <Button variant="outline" className="rounded-none gap-2 text-sm" data-testid="view-full-gallery-mobile-btn">
                View Full Gallery <ArrowUpRight size={14} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* ─── TESTIMONIALS ─── */}
      <section id="testimonials" className="py-16 sm:py-20 md:py-28 bg-slate-50 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-end justify-between mb-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest text-blue-800 mb-2">Testimonials</p>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-950" style={{ fontFamily: "'Outfit', sans-serif" }}>
                What Our Graduates Say
              </h2>
            </div>
            <Link to="/testimonials">
              <Button variant="outline" className="rounded-none gap-2 text-sm hidden sm:inline-flex" data-testid="view-all-testimonials-btn">
                View All Testimonials <ArrowUpRight size={14} />
              </Button>
            </Link>
          </div>
          <p className="text-base text-slate-600 mb-8">Cohort One graduates share their transformation stories.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="testimonials-grid">
            {LANDING_TESTIMONIALS.map((t, i) => (
              <Card key={i} className="border border-slate-200 rounded-none shadow-sm" data-testid={`landing-testimonial-${i}`}>
                <CardContent className="p-5">
                  <Quotes size={20} className="text-blue-800/20 mb-2" weight="fill" />
                  <p className="text-sm text-slate-700 leading-relaxed italic mb-4">"{t.quote}"</p>
                  <Separator className="mb-3" />
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex-shrink-0 flex items-center justify-center text-white text-xs font-bold ${
                      ["bg-blue-800","bg-slate-800","bg-emerald-700","bg-amber-700","bg-indigo-700","bg-rose-700"][i % 6]
                    }`}>
                      {t.name.split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-slate-900 truncate">{t.name}</p>
                      <p className="text-xs text-slate-500 truncate">{t.role}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="mt-8 text-center sm:hidden">
            <Link to="/testimonials">
              <Button variant="outline" className="rounded-none gap-2 text-sm" data-testid="view-all-testimonials-mobile-btn">
                View All Testimonials <ArrowUpRight size={14} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* ─── CONTACT / INTEREST FORM ─── */}
      <section id="contact" className="py-16 sm:py-20 md:py-28 bg-white border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-20">
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest text-blue-800 mb-2">Get in Touch</p>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-950 mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
                Indicate Your Interest
              </h2>
              <p className="text-base text-slate-600 mb-8 leading-relaxed">
                Ready to take the next step in your educational leadership journey? Fill out the form and our admissions team will reach out with programme details, fees, and enrollment guidance.
              </p>
              <div className="space-y-4">
                <div className="flex items-center gap-3 text-sm text-slate-700">
                  <Envelope size={20} className="text-blue-800 flex-shrink-0" />
                  <a href="mailto:info@schoolbuilderlab.net.ng" className="hover:text-blue-800 transition-colors">info@schoolbuilderlab.net.ng</a>
                </div>
                <div className="flex items-center gap-3 text-sm text-slate-700">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 256 256" className="text-blue-800 flex-shrink-0"><path fill="currentColor" d="M222.36,158.46l-47.1-21.11-.12-.06a16,16,0,0,0-15.17,1.4,8.12,8.12,0,0,0-.75.56L134.87,160c-15.42-7.49-31.34-23.29-38.83-38.51l20.78-24.71c.2-.25.39-.5.57-.77a16,16,0,0,0,1.32-15.06.61.61,0,0,1,0-.12L97.54,33.64a16,16,0,0,0-16.62-9.52A56.26,56.26,0,0,0,32,80c0,79.4,64.6,144,144,144a56.26,56.26,0,0,0,55.88-48.92A16,16,0,0,0,222.36,158.46Z"/></svg>
                  <a href="tel:+2348062553793" className="hover:text-blue-800 transition-colors">+(234) (806) 255-3793</a>
                </div>
                <div className="flex items-center gap-3 text-sm text-slate-700">
                  <MapPin size={20} className="text-blue-800 flex-shrink-0" />
                  <span>Lagos, Nigeria</span>
                </div>
                <div className="flex items-start gap-3 text-sm text-slate-700">
                  <MapPin size={20} className="text-blue-800 flex-shrink-0 mt-0.5" />
                  <span>School Builder Lab Africa<br />Lagos, Nigeria</span>
                </div>
              </div>
            </div>

            <div>
              {submitted ? (
                <Card className="border border-emerald-200 rounded-none bg-emerald-50/50">
                  <CardContent className="p-6 sm:p-8 text-center">
                    <CheckCircle size={48} weight="fill" className="text-emerald-600 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-slate-900 mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>Thank You!</h3>
                    <p className="text-sm text-slate-600">We've received your interest. Our admissions team will contact you at the email provided shortly.</p>
                  </CardContent>
                </Card>
              ) : (
                <Card className="border border-slate-200 rounded-none shadow-sm">
                  <CardContent className="p-5 sm:p-6 md:p-8">
                    <form onSubmit={handleSubmit} className="space-y-4">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Full Name *</Label>
                          <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Your full name" required className="mt-1.5 rounded-none border-slate-300" data-testid="interest-name-input" />
                        </div>
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Email Address *</Label>
                          <Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="you@example.com" required className="mt-1.5 rounded-none border-slate-300" data-testid="interest-email-input" />
                        </div>
                      </div>
                      <div>
                        <Label className="text-sm font-medium text-slate-700">Phone Number</Label>
                        <Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="+234..." className="mt-1.5 rounded-none border-slate-300" data-testid="interest-phone-input" />
                      </div>
                      <div>
                        <Label className="text-sm font-medium text-slate-700">Programme of Interest</Label>
                        <Select value={form.programme_interest} onValueChange={v => setForm({ ...form, programme_interest: v })}>
                          <SelectTrigger className="mt-1.5 rounded-none border-slate-300" data-testid="interest-programme-select"><SelectValue placeholder="Select a programme" /></SelectTrigger>
                          <SelectContent>
                            {SBL_PROGRAMMES.map(p => (
                              <SelectItem key={p} value={p}>{p}</SelectItem>
                            ))}
                            <SelectItem value="Not sure yet">Not sure yet</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-sm font-medium text-slate-700">Message (optional)</Label>
                        <Textarea value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} placeholder="Tell us about your educational leadership goals..." rows={3} className="mt-1.5 rounded-none border-slate-300" data-testid="interest-message-input" />
                      </div>
                      <Button type="submit" disabled={submitting} className="w-full bg-blue-800 text-white hover:bg-blue-900 rounded-none font-medium h-11" data-testid="interest-submit-btn">
                        {submitting ? "Submitting..." : "Submit Interest"}
                      </Button>
                      <p className="text-xs text-slate-500 text-center">
                        By submitting, you agree to be contacted by our admissions team at info@schoolbuilderlab.net.ng
                      </p>
                    </form>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ─── FOOTER ─── */}
      <footer className="bg-slate-950 border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 sm:py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div className="md:col-span-2">
              <div className="flex items-center gap-2.5 mb-4">
                <img src={SBL_LOGO} alt="SBL" className="w-9 h-9 object-contain rounded-sm" />
                <span className="text-lg font-bold tracking-tight text-white" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  School Builder Lab Africa
                </span>
              </div>
              <p className="text-sm text-slate-400 max-w-sm leading-relaxed">
                Developing the next generation of visionary education leaders through world-class educational programmes, mentorship, and transformative learning experiences.
              </p>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-4">Quick Links</p>
              <div className="space-y-2 text-sm">
                <a href="#top" className="block text-slate-400 hover:text-white transition-colors">Home</a>
                <a href="#programmes" className="block text-slate-400 hover:text-white transition-colors">Programmes</a>
                <a href="#features" className="block text-slate-400 hover:text-white transition-colors">Why Us?</a>
                <Link to="/team" className="block text-slate-400 hover:text-white transition-colors">Meet the Team</Link>
                <Link to="/team#board" className="block text-slate-400 hover:text-white transition-colors">Board of Advisors</Link>
                <Link to="/gallery" className="block text-slate-400 hover:text-white transition-colors">Gallery</Link>
                <Link to="/testimonials" className="block text-slate-400 hover:text-white transition-colors">Testimonials</Link>
                <Link to="/careers" className="block text-slate-400 hover:text-white transition-colors">Vacancies</Link>
                <a href="#contact" className="block text-slate-400 hover:text-white transition-colors">Contact</a>
              </div>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-4">Platform</p>
              <div className="space-y-2 text-sm">
                <Link to="/login" className="block text-slate-400 hover:text-white transition-colors">Student Login</Link>
                <Link to="/register" className="block text-slate-400 hover:text-white transition-colors">Create Account</Link>
                <Link to="/free-courses" className="block text-slate-400 hover:text-white transition-colors">Free Courses</Link>
                <a href="mailto:info@schoolbuilderlab.net.ng" className="block text-slate-400 hover:text-white transition-colors">info@schoolbuilderlab.net.ng</a>
                <a href="tel:+2348062553793" className="block text-slate-400 hover:text-white transition-colors">+(234) (806) 255-3793</a>
              </div>
              <div className="flex gap-3 mt-5">
                <a href="https://www.linkedin.com/company/school-builder-s-lab/?viewAsMember=true" target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-white transition-colors" data-testid="social-linkedin">
                  <LinkedinLogo size={22} weight="fill" />
                </a>
                <a href="https://www.facebook.com/share/18b77iwy1B/" target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-white transition-colors" data-testid="social-facebook">
                  <FacebookLogo size={22} weight="fill" />
                </a>
                <a href="https://www.instagram.com/schoolbuilder2025?igsh=MWpkOWxmcmwyMGdxMw==" target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-white transition-colors" data-testid="social-instagram">
                  <InstagramLogo size={22} weight="fill" />
                </a>
                <a href="https://youtube.com/@theschoolbuilderlab?si=_oJPk2rm9tAj6dJ3" target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-white transition-colors" data-testid="social-youtube">
                  <YoutubeLogo size={22} weight="fill" />
                </a>
              </div>
            </div>
          </div>
          <Separator className="bg-slate-800 mb-6" />
          <p className="text-xs text-slate-500 text-center">Powered by OA, {new Date().getFullYear()}. School Builder Lab Africa. All rights reserved.</p>
        </div>
      </footer>

      <SblChatWidget />
      <LandingFlashCard />
    </div>
  );
}
